import { useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Drawer, Button } from '@mui/material';

import { theme } from '../../theme';
import { PriceGridContext } from '../../context/PriceGridContext';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import { IUnit } from '../../types/unit';
import { selectProject } from '../../features/project/projectSlice';
import { accessAllowed } from '../../features/project/projectHooks';
import { downloadImages, numToCurrency, netHST, buildWhatsAppShareUrl, buildEmailShareUrl, isIos } from '../../utils/Functions';
import { libertyProjects } from '../../utils/Constants';
import { selectUser } from '../../features/auth/authSlice';
import FilterMenu from './popups/FilterMenu';
import { handleModal } from '../../features/modal/modalSlice';
import { useCreateActivity } from '../../features/activity/activityHooks';
import { useUploadSharedPdf } from '../../features/share/shareHooks';
import ShareRecipientDialog, { IShareRecipient, ShareMethod } from '../common/ShareRecipientDialog';
import ShareLinkDialog from '../common/ShareLinkDialog';

const GridHeader = (props: any) => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const { setShowStack, download, handleBack, setLegend, legend, handlePrint } = props;
  const {
    units,
    selectingUnits,
    setSelectingUnits,
    selectedUnits,
    setSelectedUnits,
    filteredUnits,
    setFilteredUnits,
    noUnits,
    setNoUnits,
    selectedPriceList,
    setModalType,
    draft,
    leasing,
    setLeasing,
  } = useContext(PriceGridContext);

  const [open, setOpen] = useState<boolean>(false);
  const [unitsToShare, setUnitsToShare] = useState<IUnit[] | null>(null);
  const [pendingShare, setPendingShare] = useState<{ url: string; method: ShareMethod } | null>(null);
  const createActivity = useCreateActivity();
  const uploadSharedPdf = useUploadSharedPdf();

  const [marketingFloorPlans] = useLazyQuery(GETMARKETINGFLOORPLANS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const buildMarketingUnits = (allSelected: IUnit[]) =>
    units
      .filter((unit: IUnit) => allSelected.some((selectedUnit: IUnit) => selectedUnit._id === unit._id))
      .map((unit: IUnit) => {
        return {
          _id: unit._id,
          level: unit.level,
          unit: unit.unit,
          exposure: unit.exposure,
          bathroom: unit.bathroom,
          outdoor: unit.outdoor,
          modelType: unit.modelType,
          suite: unit.suite,
          unitType: unit.unitType,
          basePrice: unit.basePrice,
          rental: unit.rental ? unit.rental : null,
          size: unit.size,
        };
      });

  const fetchMarketingFloorPlans = (allSelected: IUnit[], onSuccess: (marketingUnits: any[], floorPlans: any[]) => void) => {
    if (!allSelected.length) {
      storeDispatch(showErrorSnackbar('No Units Selected'));
      return;
    }

    const marketingUnits = buildMarketingUnits(allSelected);

    marketingFloorPlans({ variables: { units: marketingUnits, project: project._id } }).then((res) => {
      if (res.data.getMarketingFloorPlans.length > 0) {
        onSuccess(marketingUnits, res.data.getMarketingFloorPlans);
      } else {
        storeDispatch(showErrorSnackbar('No Floor Plan to Share'));
      }
    });
  };

  const downloadMarketingFloorPlans = () => {
    fetchMarketingFloorPlans(selectedUnits, (marketingUnits, floorPlans) => {
      downloadImages(floorPlans, project.email, project.name, false, project._id, project);
    });
  };

  const openShareDialog = (unitsToOpen: IUnit[]) => {
    if (!unitsToOpen.length) {
      storeDispatch(showErrorSnackbar('No Units Selected'));
      return;
    }
    setUnitsToShare(unitsToOpen);
  };

  // The merged floorplan PDF is uploaded to the bucket's shared/ folder by the backend, and the link
  // it returns is what gets sent - WhatsApp/mailto links can't attach a file. The recipient captured
  // in the dialog is for the activity log (and the email "to"), not for pre-selecting a WhatsApp chat.
  const handleRecipientShare = (recipient: IShareRecipient) => {
    const { method } = recipient;
    const shareUnits = unitsToShare || [];
    setUnitsToShare(null);

    fetchMarketingFloorPlans(shareUnits, (marketingUnits, floorPlans) => {
      const recipientLabel = recipient.contact ? `${recipient.name} (${recipient.contact})` : recipient.name;
      const unitsSummary = (separator: string) =>
        marketingUnits
          .map((unit: any) => {
            const basePrice = project.netHst ? netHST(unit.basePrice, project) : unit.basePrice;
            return `${unit.suite} - ${numToCurrency.format(basePrice)}`;
          })
          .join(separator);

      const sendLink = async (pdf: File) => {
        const url = await uploadSharedPdf(pdf, recipient.name, project._id!);
        if (!url) return false;

        const firstName = recipient.name.trim().split(/\s+/)[0];
        const message = `Hello ${firstName}, please see the link to the floorplans\n${url}`;
        const ios = isIos();
        const shareUrl =
          method === 'whatsapp'
            ? buildWhatsAppShareUrl(recipient.contact, message, ios)
            : buildEmailShareUrl(
                recipient.contact,
                `${project.name} - Floorplans`,
                message,
                libertyProjects.includes(project._id!) ? project.email : undefined
              );
        if (ios) {
          setPendingShare({ url: shareUrl, method });
        } else {
          window.location.href = shareUrl;
        }
        return true;
      };

      downloadImages(floorPlans, project.email, project.name, true, project._id, project, sendLink).then((shared: boolean) => {
        if (shared && process.env.REACT_APP_ENV !== 'local') {
          createActivity({
            project: project._id,
            user: user._id,
            units: marketingUnits.map((unit: any) => unit._id),
            title: `Units Shared - ${method === 'whatsapp' ? 'WhatsApp' : 'Email'}`,
            content: `${unitsSummary(', ')} → Shared with ${recipientLabel} via ${method === 'whatsapp' ? 'WhatsApp' : 'Email'}`,
          });
        }
      });
    });
  };

  const getAllMarketingFloorPlans = () => {
    const shareableUnits = units.filter((unit: IUnit) => unit.status === 'HL');
    openShareDialog(shareableUnits);
  };

  const handleSelectUnits = (selectingUnits: boolean) => {
    if (selectingUnits) {
      setSelectedUnits([]);
    }
    setSelectingUnits(!selectingUnits);
  };

  return (
    <div>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: {
              sm: '450px',
              xs: '100%',
            },
          },
        }}
      >
        <FilterMenu handleClose={() => setOpen(false)} />
      </Drawer>
      <Box
        sx={{
          display: 'flex',
          marginRight: '20px',
          marginLeft: 'auto',
          [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
            marginRight: 0,
          },
        }}
      >
        {user.type !== 'Developer' && user.type !== 'Marketing' && accessAllowed(user, project._id!, 'viewLeasing') && project.leasing ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color={leasing ? 'secondary' : 'primary'}
            onClick={() => setLeasing(!leasing)}
          >
            {leasing ? 'Preconstruction' : 'Leasing'}
          </Button>
        ) : null}
        {selectedUnits.length > 0 && selectingUnits && !draft ? (
          <>
            <Button
              sx={(theme) => ({
                fontSize: '11px',
                mr: 1,
                '@media (max-width:1499.98px)': {
                  fontSize: '9px',
                  padding: '3px 8px',
                  minWidth: 'auto',
                  mr: 0.75,
                },
                [theme.breakpoints.down('sm')]: {
                  mb: 1,
                },
              })}
              variant="contained"
              color="primary"
              onClick={() => downloadMarketingFloorPlans()}
            >
              Download
            </Button>
            <Button
              sx={(theme) => ({
                fontSize: '11px',
                mr: 1,
                '@media (max-width:1499.98px)': {
                  fontSize: '9px',
                  padding: '3px 8px',
                  minWidth: 'auto',
                  mr: 0.75,
                },
                [theme.breakpoints.down('sm')]: {
                  mb: 1,
                },
              })}
              variant="contained"
              color="primary"
              onClick={() => openShareDialog(selectedUnits)}
            >
              Share
            </Button>
          </>
        ) : null}
        {!noUnits && !draft ? (
          <Button
            sx={(theme) => ({
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
            })}
            variant="contained"
            onClick={() => getAllMarketingFloorPlans()}
          >
            Share All
          </Button>
        ) : null}
        <Button
          sx={(theme) => ({
            fontSize: '11px',
            mr: 1,
            '@media (max-width:1499.98px)': {
              fontSize: '9px',
              padding: '3px 8px',
              minWidth: 'auto',
              mr: 0.75,
            },
            [theme.breakpoints.down('sm')]: {
              mb: 1,
            },
          })}
          variant="contained"
          color="primary"
          onClick={() => setOpen(true)}
        >
          Filter
        </Button>
        <Button
          sx={(theme) => ({
            fontSize: '11px',
            mr: 1,
            '@media (max-width:1499.98px)': {
              fontSize: '9px',
              padding: '3px 8px',
              minWidth: 'auto',
              mr: 0.75,
            },
            [theme.breakpoints.down('sm')]: {
              mb: 1,
            },
          })}
          variant="contained"
          color="primary"
          onClick={() => {
            setModalType('labels');
            storeDispatch(handleModal(true));
          }}
        >
          Labels
        </Button>
        {filteredUnits.length || noUnits ? (
          <>
            <Button
              sx={(theme) => ({
                fontSize: '11px',
                mr: 1,
                '@media (max-width:1499.98px)': {
                  fontSize: '9px',
                  padding: '3px 8px',
                  minWidth: 'auto',
                  mr: 0.75,
                },
                [theme.breakpoints.down('sm')]: {
                  mb: 1,
                },
              })}
              variant="contained"
              color="warning"
              onClick={() => {
                setSelectingUnits(false);
                setSelectedUnits([]);
                setFilteredUnits([]);
                setNoUnits(false);
              }}
            >
              Reset Filter
            </Button>
            {selectedUnits.length !== filteredUnits.length && !noUnits ? (
              <Button
                sx={(theme) => ({
                  fontSize: '11px',
                  mr: 1,
                  '@media (max-width:1499.98px)': {
                    fontSize: '9px',
                    padding: '3px 8px',
                    minWidth: 'auto',
                    mr: 0.75,
                  },
                  [theme.breakpoints.down('sm')]: {
                    mb: 1,
                  },
                  color: '#fff',
                  backgroundColor: '#2aa1b5',
                })}
                variant="contained"
                onClick={() => {
                  setSelectingUnits(!selectingUnits);
                  setSelectedUnits(filteredUnits.map((filteredUnit: IUnit) => filteredUnit));
                }}
              >
                Select All Units
              </Button>
            ) : null}
          </>
        ) : null}
        {user.type !== 'Sales' ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                display: 'none',
              },
              [theme.breakpoints.up('sm')]: {
                display: 'block',
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => setLegend(!legend)}
          >
            Legend
          </Button>
        ) : null}
        <Button
          sx={(theme) => ({
            fontSize: '11px',
            mr: 1,
            '@media (max-width:1499.98px)': {
              fontSize: '9px',
              padding: '3px 8px',
              minWidth: 'auto',
              mr: 0.75,
            },
            [theme.breakpoints.down('sm')]: {
              mb: 1,
            },
          })}
          variant="contained"
          color={selectingUnits ? 'secondary' : 'primary'}
          onClick={() => handleSelectUnits(selectingUnits)}
        >
          Select Units
        </Button>
        {accessAllowed(user, project._id!, 'editUnits') && selectingUnits ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => {
              if (selectingUnits) {
                setModalType('editUnits');
                storeDispatch(handleModal(true));
              } else {
                storeDispatch(showErrorSnackbar('Please click Select Units then select a unit'));
              }
            }}
          >
            Edit Selected
          </Button>
        ) : null}
        {project._id !== '61535b00cb68b4620011d555' && user.type !== 'Sales' ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => setShowStack(true)}
          >
            Stack
          </Button>
        ) : null}
        {user.type === 'Manager' ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => {
              setModalType('priceList');
              storeDispatch(handleModal(true));
            }}
          >
            Drafts
          </Button>
        ) : null}
        {user.type === 'Manager' || user.type === 'Admin' ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => {
              setModalType('uploadExcel');
              storeDispatch(handleModal(true));
            }}
          >
            Upload Excel
          </Button>
        ) : null}
        {accessAllowed(user, project._id!, 'createUnits') ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => {
              setModalType('create');
              storeDispatch(handleModal(true));
            }}
          >
            Create Unit
          </Button>
        ) : null}
        {user.type === 'Manager' && draft ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => download(selectedPriceList)}
          >
            Download Reports
          </Button>
        ) : null}
        {user.type === 'Manager' && draft && selectedPriceList ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="success"
            onClick={() => {
              setModalType('publishPriceList');
              storeDispatch(handleModal(true));
            }}
          >
            Publish
          </Button>
        ) : null}
        {user.type === 'Manager' && draft ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => handleBack()}
          >
            Back
          </Button>
        ) : null}
        {user.type === 'Manager' ? (
          <Button
            sx={(theme) => ({
              fontSize: '11px',
              mr: 1,
              '@media (max-width:1499.98px)': {
                fontSize: '9px',
                padding: '3px 8px',
                minWidth: 'auto',
                mr: 0.75,
              },
              [theme.breakpoints.down('sm')]: {
                mb: 1,
              },
            })}
            variant="contained"
            color="primary"
            onClick={() => handlePrint()}
          >
            Print
          </Button>
        ) : null}
      </Box>
      <ShareRecipientDialog
        open={!!unitsToShare}
        suites={(unitsToShare || []).map((unit: IUnit) => unit.suite)}
        onClose={() => setUnitsToShare(null)} onSubmit={handleRecipientShare} />
      <ShareLinkDialog share={pendingShare} onClose={() => setPendingShare(null)} />
    </div>
  );
};

const GETMARKETINGFLOORPLANS = gql`
  query getMarketingFloorPlans($units: [NewMarketingInput!], $project: MongoID!) {
    getMarketingFloorPlans(units: $units, project: $project) {
      _id
      level
      unit
      suite
      modelType
      unitType
      bathroom
      exposure
      outdoor {
        name
        size
      }
      size
      rental
      marketingFloorPlan
      pdfFloorPlan
      basePrice
    }
  }
`;

export default GridHeader;
